import { createContext, ReactNode, useCallback, useEffect, useState, useContext } from 'react';
import { CurrentUserStateContext } from './user';
import useGetCountryDataByCodesLazy from 'basics/graphql/queries/getCountryDataByCodesLazy';
import { CountryDataType } from 'basics/types/countryData.types';
import { errorToast } from 'basics/utils/toast';
import { CountryDataFilter, InitiativeCountry } from 'generated/maya.types';

export type CountriesContextType = {
  countries: CountryDataType[];
  countriesLoading: boolean;
};

export const CountriesContext = createContext<CountriesContextType>({
  countries: [],
  countriesLoading: false,
});

export const CountriesProvider = ({ children }: { children: ReactNode }) => {
  const [countries, setCountries] = useState<CountryDataType[]>([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const { getCountryDataByCodesLazy } = useGetCountryDataByCodesLazy();
  const userContextValue = useContext(CurrentUserStateContext);

  const getCountriesData = useCallback(async () => {
    try {
      const { currentUser } = userContextValue.currentUserData;

      if (currentUser) {
        setCountriesLoading(true);
        const countryCodes = Object.keys(InitiativeCountry) as Array<keyof typeof InitiativeCountry>;
        const countryFilter: CountryDataFilter = { code: countryCodes };
        const { data } = await getCountryDataByCodesLazy({ variables: { filters: countryFilter } });

        if (data?.getCountryDataByCodes) {
          setCountries(data.getCountryDataByCodes);
        }
      }
    } catch (error) {
      errorToast(error as string);
    } finally {
      setCountriesLoading(false);
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [
    userContextValue.currentUserData.loading,
  ]);

  useEffect(() => {
    getCountriesData();
  }, [getCountriesData]);

  return (
    <CountriesContext.Provider value={ { countries, countriesLoading } }>
      { children }
    </CountriesContext.Provider>
  );
};
